import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
    KeyboardEvent,
    useRef,
} from 'react';
import { Box, Group, Text } from '@mantine/core';
import { createStyles } from '@mantine/styles';
import { useTranslation } from 'react-i18next';
import UserAvatar from '@/common/components/UserAvatars/UserAvatar';
import { UserBase } from '@tickitacka/shared';

export interface MentionListProps {
    items: UserBase[];
    command: (props: { id: string; label: string }) => void;
}

const useStyles = createStyles(theme => ({
    mentionItem: {
        cursor: 'pointer',

        '&:hover': {
            backgroundColor: theme.colors.gray[0],
        },

        '&[data-selected="true"]': {
            backgroundColor: theme.colors.gray[1],
        },
    },
}));

const MentionList = forwardRef((props: MentionListProps, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { classes } = useStyles();
    const { t } = useTranslation('comment');
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const selectItem = (index: number) => {
        const item = props.items[index];

        if (item) {
            props.command({ id: item.id, label: item.name });
        }
    };

    const upHandler = () => {
        setSelectedIndex(
            (selectedIndex + props.items.length - 1) % props.items.length
        );
    };

    const downHandler = () => {
        setSelectedIndex((selectedIndex + 1) % props.items.length);
    };

    const enterHandler = () => {
        selectItem(selectedIndex);
    };

    useEffect(() => {
        setSelectedIndex(0);
        // 아이템 배열이 변경될 때마다 참조 배열 초기화
        itemRefs.current = itemRefs.current.slice(0, props.items.length);
    }, [props.items]);

    // 선택된 인덱스가 변경될 때 해당 요소로 스크롤
    useEffect(() => {
        if (itemRefs.current[selectedIndex]) {
            itemRefs.current[selectedIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            if (event.key === 'ArrowUp') {
                upHandler();
                return true;
            }

            if (event.key === 'ArrowDown') {
                downHandler();
                return true;
            }

            if (event.key === 'Enter') {
                enterHandler();
                return true;
            }

            return false;
        },
    }));

    return (
        <Box
            style={{
                borderRadius: '4px',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                padding: '4px',
                overflow: 'auto',
            }}
            mah={188}
        >
            {props.items.length ? (
                props.items.map((item, index) => (
                    <Box
                        key={index}
                        ref={el => (itemRefs.current[index] = el)}
                        onClick={() => selectItem(index)}
                        data-selected={selectedIndex === index}
                        className={classes.mentionItem}
                        px="sm"
                        py={8}
                        fz="sm"
                        style={{
                            borderRadius: '4px',
                        }}
                    >
                        <Group gap={5}>
                            <UserAvatar
                                name={item.name}
                                imageUrl={item.profile_image_url}
                            />
                            <Text size="sm">{item.name}</Text>
                        </Group>
                    </Box>
                ))
            ) : (
                <Box p="xs" fz="xs">
                    {t('comment.no_user')}
                </Box>
            )}
        </Box>
    );
});

MentionList.displayName = 'MentionList';

export default MentionList;
